import {
  createElement,
  lazy,
  useEffect,
  useState,
  type ComponentType,
  type ReactNode,
} from 'react';
import type {
  PluginWebRegistration,
  PluginWebManifest,
  PluginIconComponent,
  AuthProviderWebConfig,
} from './types/web.js';
import { collectComponentPaths } from './manifest-utils.js';

/**
 * Wraps a dynamic import in a component that renders `null` until the module
 * resolves, then swaps the real component in — without ever suspending.
 *
 * Slot components (badges, chips, global UI elements, docs hints, sections)
 * must use this instead of `React.lazy()`: a `lazy()` slot that is still
 * loading when a synchronous render reaches it (e.g. a `useSyncExternalStore`
 * update from TanStack Query) suspends the whole sync render, and React's
 * immediate-retry loop for suspended sync work starves the event loop — the
 * import can then never resolve and the tab livelocks at 100% CPU.
 *
 * Loading starts on the first render (not at creation time), so importing a
 * `register.ts` under Node never pulls in browser-only modules.
 *
 * The returned component reserves one prop: `slotFallback` (a `ReactNode`)
 * renders in place of the component while the module is still loading, for
 * slots whose absence would drop meaningful content (e.g. content scanners
 * that must keep showing the matched text). It is stripped before the real
 * component receives its props.
 */
export function createSlotComponent(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  load: () => Promise<{ default: ComponentType<any> }>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): ComponentType<any> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let resolved: ComponentType<any> | null = null;
  let loading: Promise<void> | null = null;
  const start = () =>
    (loading ??= load().then(
      (m) => {
        resolved = m.default;
      },
      (error: unknown) => {
        // A failed slot import degrades to rendering nothing; slots are
        // optional decorations, so surface the failure without crashing.
        console.error('[createSlotComponent] failed to load slot component', error);
      },
    ));
  return function SlotComponent({ slotFallback, ...props }: Record<string, unknown>) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [Component, setComponent] = useState<ComponentType<any> | null>(() => resolved);
    useEffect(() => {
      if (Component) return;
      let active = true;
      void start().then(() => {
        if (active && resolved) setComponent(() => resolved);
      });
      return () => {
        active = false;
      };
    }, [Component]);
    if (!Component) return (slotFallback as ReactNode) ?? null;
    return createElement(Component, props);
  };
}

/**
 * Reduces boilerplate in plugin `register.ts` files by auto-wrapping
 * component imports in `React.lazy()` with the standard `.then(m => ({ default: m.X }))`
 * pattern. The component name is inferred from the last segment of the key.
 *
 * The `icons` parameter accepts bare `ComponentType` so custom SVG components
 * (e.g. `(props: SVGProps<SVGSVGElement>) => JSX.Element`) can be registered
 * without `as never` casts.
 *
 * @example
 * import { Plug } from 'lucide-react';
 * import { createWebRegistration } from '@reflex/plugin-api/create-web-registration';
 *
 * export default createWebRegistration({
 *   components: {
 *     '@reflex/plugin-mcp/web/McpServersPage': () => import('./McpServersPage'),
 *     '@reflex/plugin-mcp/web/McpDetailSection': () => import('./McpDetailSection'),
 *   },
 *   icons: { Plug },
 * });
 */
export function createWebRegistration(config: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  components?: Record<string, () => Promise<any>>;
  /**
   * Like `components`, but wrapped with {@link createSlotComponent} instead of
   * `React.lazy()`: the component renders `null` until its module resolves and
   * never suspends. Use for inline slot components (badges, global UI
   * elements, sections) — anything rendered inside an already-visible page.
   * Route pages belong in `components` so navigation keeps its Suspense
   * fallback (the top progress bar).
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  slotComponents?: Record<string, () => Promise<any>>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  icons?: Record<string, ComponentType<any>>;
  authProviders?: Record<string, AuthProviderWebConfig>;
  /**
   * Optional snapshot of the plugin's web manifest. When provided, the SPA
   * resolves public routes (and other manifest-derived metadata) from the
   * bundled registration directly — bypassing the auth-gated
   * `GET /api/config/plugins` HTTP fetch so anonymous visitors can hit
   * marketing pages and other public routes immediately. Plugins with
   * `public: true` routes should always set this.
   */
  manifest?: PluginWebManifest;
}): PluginWebRegistration {
  const components: Record<string, ComponentType> = {};

  const normalize = (key: string, loader: () => Promise<unknown>) => () =>
    loader().then((m) => {
      const name = key.split('/').pop()!;
      const moduleRecord = m as Record<string, ComponentType> | undefined;
      // Guard against a module that resolved to null/undefined (an empty
      // chunk, a stale build cache, or a circular import that hasn't finished
      // initializing). Without this guard, the `moduleRecord[name]` access
      // throws a cryptic "Cannot read properties of undefined (reading
      // '<Name>')" TypeError that crashes the route's ErrorBoundary with no
      // indication of which plugin component failed — see Sentry
      // AGENT-FLOW-WEB-4S, where a flow-editor page white-screened because its
      // dynamic import resolved to `undefined`. When no named, default, or
      // first-export fallback can be resolved either, reject with a
      // descriptive error so the ErrorBoundary and Sentry both name the
      // failing component instead of surfacing the raw TypeError.
      const component = moduleRecord
        ? (moduleRecord[name] ?? moduleRecord.default ?? Object.values(moduleRecord)[0] ?? null)
        : null;
      if (!component) {
        const exportedNames = moduleRecord ? Object.keys(moduleRecord) : [];
        throw new Error(
          `Plugin component '${key}' could not be resolved: dynamic import ` +
            `${exportedNames.length ? `exported [${exportedNames.join(', ')}]` : 'resolved to an empty module'}; ` +
            `expected a '${name}' or 'default' export.`,
        );
      }
      return { default: component as ComponentType };
    });

  for (const [key, loader] of Object.entries(config.components ?? {})) {
    components[key] = lazy(normalize(key, loader));
  }
  for (const [key, loader] of Object.entries(config.slotComponents ?? {})) {
    components[key] = createSlotComponent(normalize(key, loader));
  }

  return {
    components,
    icons: (config.icons ?? {}) as Record<string, PluginIconComponent>,
    authProviders: config.authProviders,
    manifest: config.manifest,
  };
}

/**
 * Builds a `PluginWebRegistration` by deriving the full `componentPath` keys
 * from the plugin's web manifest. Instead of duplicating long strings like
 * `'@reflex/plugin-cost/web/CostPage'` in both `index.ts` and `register.ts`,
 * you pass short component names that are matched against the last segment of
 * each `componentPath` in the manifest.
 *
 * @param manifest   - The plugin's `web` manifest (from `definePlugin`)
 * @param imports    - Map of short component names to dynamic import functions
 * @param options    - Icons and optional auth providers
 *
 * @example
 * import { DollarSign, BarChart3 } from 'lucide-react';
 * import { createWebRegistrationFromManifest } from '@reflex/plugin-api/create-web-registration';
 * import { costPlugin } from '../index.js';
 *
 * export default createWebRegistrationFromManifest(costPlugin.web!, {
 *   CostPage: () => import('./CostPage'),
 *   CostsBadge: () => import('./CostsBadge'),
 *   UsageSection: () => import('./UsageSection'),
 *   HomeStatWidget: () => import('./HomeStatWidget'),
 * }, {
 *   icons: { DollarSign, BarChart3 },
 * });
 */
export function createWebRegistrationFromManifest(
  manifest: PluginWebManifest,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  imports: Record<string, () => Promise<any>>,
  options?: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    icons?: Record<string, ComponentType<any>>;
    authProviders?: Record<string, AuthProviderWebConfig>;
  },
): PluginWebRegistration {
  // Route pages stay `React.lazy()` so navigations render the route Suspense
  // fallback (top progress bar). Every other manifest surface is an inline
  // slot and must never suspend — see `createSlotComponent`.
  const routePaths = new Set((manifest.routes ?? []).map((r) => r.componentPath));
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const fullPathComponents: Record<string, () => Promise<any>> = {};
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const fullPathSlotComponents: Record<string, () => Promise<any>> = {};
  const allPaths = collectComponentPaths(manifest);

  for (const fullPath of allPaths) {
    const shortName = fullPath.split('/').pop()!;
    const loader = imports[shortName];
    if (!loader) {
      // Warn instead of throw so a stale dev-server cache or a half-landed
      // change (manifest updated before register.ts, or vice versa) doesn't
      // crash the entire app. `collectPluginWebWiringIssues` and
      // `resolveComponent` surface missing components visibly to the user.
      console.warn(
        `[createWebRegistrationFromManifest] no import for '${shortName}' (from componentPath '${fullPath}'). ` +
          `Available imports: [${Object.keys(imports).join(', ')}]. ` +
          `If this persists, restart the dev server.`,
      );
      continue;
    }
    if (routePaths.has(fullPath)) {
      fullPathComponents[fullPath] = loader;
    } else {
      fullPathSlotComponents[fullPath] = loader;
    }
  }

  return {
    ...createWebRegistration({
      components: fullPathComponents,
      slotComponents: fullPathSlotComponents,
      icons: options?.icons,
      authProviders: options?.authProviders,
    }),
    manifest,
  };
}
